import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useConfirm } from "../components/ConfirmDialog";

export default function Admin() {
  const { user } = useAuth();
  const confirm = useConfirm();
  const [tab, setTab] = useState("profiles"); // profiles | users | moderation
  const [profilesStats, setProfilesStats] = useState({ total: 0, pendientes: 0, aprobados: 0, rechazados: 0 });
  const [usersStats, setUsersStats] = useState({ total: 0, admins: 0, padres: 0, profesionales: 0 });
  const [modStats, setModStats] = useState({ total: 0, top_offenders: [] });
  const [flaggedMessages, setFlaggedMessages] = useState([]);
  const [selectedFlagged, setSelectedFlagged] = useState(new Set());
  const [clearOlderDays, setClearOlderDays] = useState("0"); // 0 = todos
  const [profileFilter, setProfileFilter] = useState("todos");
  const [profiles, setProfiles] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadStats = async () => {
    const [a, b, c] = await Promise.all([
      api.get("/admin/profiles/stats"),
      api.get("/admin/users/stats"),
      api.get("/admin/moderation/stats"),
    ]);
    setProfilesStats(a.data);
    setUsersStats(b.data);
    setModStats(c.data);
  };

  const loadProfiles = async () => {
    setLoading(true);
    try {
      const params = profileFilter !== "todos" ? { status_filter: profileFilter } : {};
      const { data } = await api.get("/admin/profiles", { params });
      setProfiles(data);
    } finally { setLoading(false); }
  };

  const loadUsers = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/users");
      setUsers(data);
    } finally { setLoading(false); }
  };

  const loadFlagged = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/moderation/messages");
      setFlaggedMessages(data);
      setSelectedFlagged(new Set());
    } finally { setLoading(false); }
  };

  useEffect(() => { loadStats(); }, []);
  useEffect(() => { if (tab === "profiles") loadProfiles(); /* eslint-disable-next-line */ }, [tab, profileFilter]);
  useEffect(() => { if (tab === "users") loadUsers(); /* eslint-disable-next-line */ }, [tab]);
  useEffect(() => { if (tab === "moderation") loadFlagged(); /* eslint-disable-next-line */ }, [tab]);

  const approveProfile = async (id) => {
    await api.post(`/admin/profiles/${id}/approve`);
    await Promise.all([loadProfiles(), loadStats()]);
  };
  const rejectProfile = async (id) => {
    await api.post(`/admin/profiles/${id}/reject`);
    await Promise.all([loadProfiles(), loadStats()]);
  };
  const deleteProfile = async (id) => {
    const ok = await confirm({ title: "¿Eliminar este perfil?", message: "Esta acción no se puede deshacer.", confirmLabel: "Eliminar" });
    if (!ok) return;
    await api.delete(`/profiles/${id}`);
    await Promise.all([loadProfiles(), loadStats()]);
  };
  const toggleAdmin = async (id) => {
    await api.post(`/admin/users/${id}/toggle-admin`);
    await Promise.all([loadUsers(), loadStats()]);
  };
  const deleteUser = async (id) => {
    const ok = await confirm({ title: "¿Eliminar este usuario?", message: "Se eliminarán también todos sus datos (perfil, favoritos, mensajes).", confirmLabel: "Eliminar" });
    if (!ok) return;
    try {
      await api.delete(`/admin/users/${id}`);
      await Promise.all([loadUsers(), loadStats()]);
    } catch (e) {
      window.alert(e.response?.data?.detail || "Error");
    }
  };

  const toggleSuspend = async (id) => {
    await api.post(`/admin/users/${id}/toggle-suspend`);
    await Promise.all([loadUsers(), loadStats()]);
  };

  const deleteFlagged = async (id) => {
    const ok = await confirm({ title: "¿Eliminar este registro de moderación?", message: "Sólo se borra el log; el usuario no se ve afectado.", confirmLabel: "Eliminar" });
    if (!ok) return;
    await api.delete(`/admin/moderation/messages/${id}`);
    await Promise.all([loadFlagged(), loadStats()]);
  };

  const toggleSelectFlagged = (id) => {
    setSelectedFlagged((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAllFlagged = () => {
    setSelectedFlagged((prev) =>
      prev.size === flaggedMessages.length ? new Set() : new Set(flaggedMessages.map((m) => m.id))
    );
  };

  const bulkDeleteFlagged = async () => {
    if (selectedFlagged.size === 0) return;
    const ok = await confirm({
      title: `¿Eliminar ${selectedFlagged.size} mensaje(s)?`,
      message: "Sólo se borran los logs seleccionados; los usuarios no se ven afectados.",
      confirmLabel: "Eliminar seleccionados",
    });
    if (!ok) return;
    await api.post("/admin/moderation/messages/bulk-delete", { ids: Array.from(selectedFlagged) });
    await Promise.all([loadFlagged(), loadStats()]);
  };

  const clearAllFlagged = async () => {
    if (flaggedMessages.length === 0) return;
    const days = parseInt(clearOlderDays, 10) || 0;
    const labelMap = { 0: "TODOS los mensajes bloqueados", 7: "los mensajes con más de 7 días", 30: "los mensajes con más de 30 días", 90: "los mensajes con más de 90 días" };
    const target = labelMap[days] || `los mensajes con más de ${days} días`;
    const ok = await confirm({
      title: `¿Vaciar ${target}?`,
      message: "Se eliminarán los registros de moderación que coincidan. Esta acción no se puede deshacer.",
      confirmLabel: "Vaciar",
    });
    if (!ok) return;
    const url = days > 0 ? `/admin/moderation/messages?older_than_days=${days}` : "/admin/moderation/messages";
    await api.delete(url);
    await Promise.all([loadFlagged(), loadStats()]);
  };

  const suspendOffender = async (userId) => {
    const ok = await confirm({ title: "¿Suspender / reactivar a este usuario?", message: "El usuario suspendido no podrá enviar mensajes.", confirmLabel: "Confirmar", danger: false });
    if (!ok) return;
    await api.post(`/admin/users/${userId}/toggle-suspend`);
    await loadStats();
  };

  if (user && user.role !== "admin") {
    return <div className="text-center py-16 text-slate-500" data-testid="admin-denied">No tienes permisos de administrador</div>;
  }

  return (
    <div className="space-y-5 animate-fade-in-up">
      <h1 className="text-2xl md:text-3xl font-extrabold">Panel de Admin</h1>

      {/* Stats */}
      {tab === "profiles" ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard label="Total" value={profilesStats.total} color="slate" testid="stat-total" />
          <StatCard label="Pendientes" value={profilesStats.pendientes} color="amber" testid="stat-pendientes" />
          <StatCard label="Aprobados" value={profilesStats.aprobados} color="green" testid="stat-aprobados" />
          <StatCard label="Rechazados" value={profilesStats.rechazados} color="red" testid="stat-rechazados" />
        </div>
      ) : tab === "users" ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard label="Total usuarios" value={usersStats.total} color="slate" testid="stat-users-total" />
          <StatCard label="Padres/Madres" value={usersStats.padres} color="green" testid="stat-padres" />
          <StatCard label="Profesionales" value={usersStats.profesionales} color="blue" testid="stat-profesionales" />
          <StatCard label="Admins" value={usersStats.admins} color="amber" testid="stat-admins" />
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3">
          <StatCard label="Mensajes bloqueados" value={modStats.total} color="red" testid="stat-mod-total" />
          <StatCard label="Reincidentes" value={modStats.top_offenders?.length || 0} color="amber" testid="stat-mod-offenders" />
        </div>
      )}

      {/* Tabs */}
      <div className="grid grid-cols-3 gap-2">
        <button onClick={() => setTab("profiles")} data-testid="tab-profiles"
          className={`py-3 rounded-2xl font-semibold border-2 transition ${tab === "profiles" ? "border-blue-600 bg-blue-50 text-blue-700" : "border-slate-200 bg-white text-slate-600"}`}>
          <i className="fa-solid fa-users mr-2"></i>Perfiles
        </button>
        <button onClick={() => setTab("users")} data-testid="tab-users"
          className={`py-3 rounded-2xl font-semibold border-2 transition ${tab === "users" ? "border-blue-600 bg-blue-50 text-blue-700" : "border-slate-200 bg-white text-slate-600"}`}>
          <i className="fa-solid fa-user mr-2"></i>Usuarios
        </button>
        <button onClick={() => setTab("moderation")} data-testid="tab-moderation"
          className={`py-3 rounded-2xl font-semibold border-2 transition ${tab === "moderation" ? "border-blue-600 bg-blue-50 text-blue-700" : "border-slate-200 bg-white text-slate-600"}`}>
          <i className="fa-solid fa-shield-halved mr-2"></i>Moderación
          {modStats.total > 0 && (
            <span className="ml-2 inline-block px-2 py-0.5 rounded-full text-xs bg-red-500 text-white" data-testid="mod-badge">{modStats.total}</span>
          )}
        </button>
      </div>

      {tab === "profiles" && (
        <>
          <div className="grid grid-cols-4 gap-2">
            {[
              { v: "todos", label: "Todos" },
              { v: "pendiente", label: "⏳ Pend." },
              { v: "aprobado", label: "✅ Aprob." },
              { v: "rechazado", label: "❌ Rech." },
            ].map((s) => (
              <button key={s.v} onClick={() => setProfileFilter(s.v)} data-testid={`filter-${s.v}`}
                className={`py-2 rounded-full text-sm font-semibold border transition ${profileFilter === s.v ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200"}`}>
                {s.label}
              </button>
            ))}
          </div>

          {loading ? <div className="text-center py-10 text-slate-500">Cargando…</div> : (
            <div className="space-y-3">
              {profiles.map((p) => (
                <div key={p.id} className="kibli-card p-4 flex items-start gap-3" data-testid={`admin-profile-${p.id}`}>
                  <div className="w-12 h-12 rounded-full overflow-hidden bg-blue-600 text-white flex items-center justify-center font-bold flex-shrink-0">
                    {p.photo_url ? <img src={p.photo_url} alt={p.name} className="w-full h-full object-cover" /> : p.name?.substring(0, 2).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold truncate">{p.name}</div>
                    <div className="text-sm text-slate-500 truncate">
                      {p.type === "canguro" ? "👶" : "🎓"} {p.type === "canguro" ? "Canguro" : "Profesor"} · {(p.zonas && p.zonas[0]) || p.ciudad} · {p.price_per_hour}€/h
                    </div>
                    <div className="mt-1">
                      <span className={`inline-block text-xs px-2 py-0.5 rounded-full font-semibold ${
                        p.status === "aprobado" ? "bg-green-100 text-green-700"
                        : p.status === "rechazado" ? "bg-red-100 text-red-700"
                        : "bg-amber-100 text-amber-700"}`} data-testid={`status-${p.id}`}>
                        {p.status}
                      </span>
                    </div>
                    <div className="mt-3 flex flex-wrap gap-2">
                      {p.status !== "aprobado" && (
                        <button onClick={() => approveProfile(p.id)} data-testid={`approve-${p.id}`} className="px-4 py-1.5 rounded-full bg-green-500 text-white text-sm font-semibold hover:bg-green-600">
                          <i className="fa-solid fa-check mr-1"></i>Aprobar
                        </button>
                      )}
                      {p.status !== "rechazado" && (
                        <button onClick={() => rejectProfile(p.id)} data-testid={`reject-${p.id}`} className="px-4 py-1.5 rounded-full bg-amber-500 text-white text-sm font-semibold hover:bg-amber-600">
                          <i className="fa-solid fa-xmark mr-1"></i>Rechazar
                        </button>
                      )}
                      <button onClick={() => deleteProfile(p.id)} data-testid={`delete-prof-${p.id}`} className="px-4 py-1.5 rounded-full bg-red-500 text-white text-sm font-semibold hover:bg-red-600">
                        <i className="fa-solid fa-trash mr-1"></i>Eliminar
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {tab === "users" && (
        loading ? <div className="text-center py-10 text-slate-500">Cargando…</div> : (
          <div className="space-y-3">
            {users.map((u) => (
              <div key={u.id} className="kibli-card p-4 flex items-start gap-3" data-testid={`admin-user-${u.id}`}>
                <div className={`w-12 h-12 rounded-full text-white flex items-center justify-center font-bold flex-shrink-0 ${u.role === "admin" ? "bg-amber-500" : "bg-blue-600"}`}>
                  {u.name?.substring(0, 1).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-bold">{u.name}</div>
                  <div className="text-sm text-slate-500 truncate">{u.email}</div>
                  <div className="mt-1 flex flex-wrap gap-1">
                    <span className={`inline-block text-xs px-2 py-0.5 rounded-full font-semibold ${
                      u.role === "admin" ? "bg-amber-100 text-amber-700"
                      : u.role === "profesional" ? "bg-blue-100 text-blue-700"
                      : "bg-slate-100 text-slate-700"}`}>
                      {u.role === "admin" ? "👑 Admin" : u.role === "profesional" ? "💼 Profesional" : "👤 Padre/Madre"}
                    </span>
                    {u.suspended && (
                      <span className="inline-block text-xs px-2 py-0.5 rounded-full font-semibold bg-red-100 text-red-700" data-testid={`suspended-badge-${u.id}`}>
                        🚫 Suspendido
                      </span>
                    )}
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <button onClick={() => toggleAdmin(u.id)} data-testid={`toggle-admin-${u.id}`} className={`px-4 py-1.5 rounded-full text-white text-sm font-semibold ${u.role === "admin" ? "bg-slate-500 hover:bg-slate-600" : "bg-blue-600 hover:bg-blue-700"}`}>
                      <i className="fa-solid fa-shield-halved mr-1"></i>{u.role === "admin" ? "Quitar admin" : "Hacer admin"}
                    </button>
                    {u.id !== user.id && (
                      <button onClick={() => toggleSuspend(u.id)} data-testid={`toggle-suspend-${u.id}`} className={`px-4 py-1.5 rounded-full text-white text-sm font-semibold ${u.suspended ? "bg-green-500 hover:bg-green-600" : "bg-amber-500 hover:bg-amber-600"}`}>
                        <i className={`fa-solid ${u.suspended ? "fa-circle-check" : "fa-ban"} mr-1`}></i>{u.suspended ? "Reactivar" : "Suspender"}
                      </button>
                    )}
                    {u.id !== user.id && (
                      <button onClick={() => deleteUser(u.id)} data-testid={`delete-user-${u.id}`} className="px-4 py-1.5 rounded-full bg-red-500 text-white text-sm font-semibold hover:bg-red-600">
                        <i className="fa-solid fa-trash mr-1"></i>Eliminar
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )
      )}

      {tab === "moderation" && (
        loading ? <div className="text-center py-10 text-slate-500">Cargando…</div> : (
          <div className="space-y-4">
            {/* Top offenders */}
            {modStats.top_offenders && modStats.top_offenders.length > 0 && (
              <div className="kibli-card p-4">
                <div className="font-bold mb-3 flex items-center gap-2">
                  <i className="fa-solid fa-triangle-exclamation text-amber-500"></i>
                  Usuarios reincidentes
                </div>
                <div className="space-y-2">
                  {modStats.top_offenders.map((o) => (
                    <div key={o.user_id} className="flex items-center gap-3 p-3 bg-amber-50 rounded-xl" data-testid={`offender-${o.user_id}`}>
                      <div className="w-10 h-10 rounded-full bg-amber-500 text-white flex items-center justify-center font-bold">
                        {o.name?.substring(0, 1).toUpperCase() || "?"}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold truncate">{o.name}</div>
                        <div className="text-xs text-slate-500 truncate">{o.email}</div>
                      </div>
                      <span className="px-3 py-1 rounded-full bg-red-500 text-white text-sm font-bold" data-testid={`offender-count-${o.user_id}`}>
                        {o.count} intentos
                      </span>
                      <button onClick={() => suspendOffender(o.user_id)} data-testid={`offender-suspend-${o.user_id}`}
                        className={`px-3 py-1.5 rounded-full text-white text-sm font-semibold ${o.suspended ? "bg-green-500 hover:bg-green-600" : "bg-amber-500 hover:bg-amber-600"}`}>
                        <i className={`fa-solid ${o.suspended ? "fa-circle-check" : "fa-ban"} mr-1`}></i>
                        {o.suspended ? "Reactivar" : "Suspender"}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Flagged messages list */}
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="font-bold flex items-center gap-2">
                <i className="fa-solid fa-shield-halved text-red-500"></i>
                Mensajes bloqueados por anti-spam
              </div>
              {flaggedMessages.length > 0 && (
                <div className="flex items-center gap-2 flex-wrap" data-testid="flagged-actions-bar">
                  <button
                    onClick={toggleSelectAllFlagged}
                    data-testid="flagged-select-all"
                    className="px-3 py-1.5 rounded-full bg-slate-100 text-slate-700 text-sm font-semibold hover:bg-slate-200">
                    <i className="fa-solid fa-check-double mr-1"></i>
                    {selectedFlagged.size === flaggedMessages.length ? "Deseleccionar todo" : "Seleccionar todo"}
                  </button>
                  <button
                    onClick={bulkDeleteFlagged}
                    disabled={selectedFlagged.size === 0}
                    data-testid="flagged-bulk-delete"
                    className="px-3 py-1.5 rounded-full bg-amber-500 text-white text-sm font-semibold hover:bg-amber-600 disabled:opacity-40 disabled:cursor-not-allowed">
                    <i className="fa-solid fa-trash mr-1"></i>
                    Eliminar seleccionados{selectedFlagged.size > 0 ? ` (${selectedFlagged.size})` : ""}
                  </button>
                  <div className="flex items-center gap-1">
                    <select
                      value={clearOlderDays}
                      onChange={(e) => setClearOlderDays(e.target.value)}
                      data-testid="flagged-clear-filter"
                      className="px-3 py-1.5 rounded-full bg-white border-2 border-red-300 text-red-700 text-sm font-semibold focus:outline-none focus:border-red-500 cursor-pointer">
                      <option value="0">Todos</option>
                      <option value="7">Más de 7 días</option>
                      <option value="30">Más de 30 días</option>
                      <option value="90">Más de 90 días</option>
                    </select>
                    <button
                      onClick={clearAllFlagged}
                      data-testid="flagged-clear-all"
                      className="px-3 py-1.5 rounded-full bg-red-500 text-white text-sm font-semibold hover:bg-red-600">
                      <i className="fa-solid fa-broom mr-1"></i>
                      Vaciar
                    </button>
                  </div>
                </div>
              )}
            </div>
            {flaggedMessages.length === 0 ? (
              <div className="text-center py-10 text-slate-500" data-testid="moderation-empty">
                <i className="fa-solid fa-circle-check text-green-500 text-4xl mb-2"></i>
                <p className="font-bold">Sin incidencias</p>
                <p className="text-sm">No se ha bloqueado ningún mensaje todavía</p>
              </div>
            ) : (
              <div className="space-y-3">
                {flaggedMessages.map((m) => (
                  <div key={m.id} className="kibli-card p-4" data-testid={`flagged-msg-${m.id}`}>
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <div className="flex items-start gap-3 min-w-0 flex-1">
                        <input
                          type="checkbox"
                          checked={selectedFlagged.has(m.id)}
                          onChange={() => toggleSelectFlagged(m.id)}
                          data-testid={`flagged-select-${m.id}`}
                          className="mt-1 w-4 h-4 accent-blue-600 cursor-pointer flex-shrink-0"
                          aria-label="Seleccionar mensaje"
                        />
                        <div className="min-w-0">
                          <div className="text-sm font-semibold text-slate-900">
                            <i className="fa-solid fa-arrow-right text-slate-400 mx-1"></i>
                            {m.from_user?.name} <span className="text-slate-400 text-xs">({m.from_user?.email})</span>
                          </div>
                          <div className="text-xs text-slate-500 mt-0.5">
                            a <span className="font-semibold">{m.to_user?.name}</span>
                            <span className="mx-2">·</span>
                            {new Date(m.created_at).toLocaleString("es-ES")}
                          </div>
                        </div>
                      </div>
                      <button onClick={() => deleteFlagged(m.id)} data-testid={`flagged-delete-${m.id}`}
                        className="text-red-500 hover:bg-red-50 w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0">
                        <i className="fa-solid fa-trash"></i>
                      </button>
                    </div>
                    <div className="p-3 rounded-xl bg-slate-50 border border-slate-200 text-sm text-slate-800 font-mono break-words">
                      "{m.text}"
                    </div>
                    <div className="mt-2 inline-flex items-center gap-1 px-3 py-1 rounded-full bg-red-100 text-red-700 text-xs font-semibold">
                      <i className="fa-solid fa-circle-exclamation"></i>{m.reason}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )
      )}
    </div>
  );
}

function StatCard({ label, value, color, testid }) {
  const colors = {
    slate: "border-slate-300 text-slate-900",
    amber: "border-amber-400 text-amber-600",
    green: "border-green-500 text-green-600",
    red: "border-red-500 text-red-600",
    blue: "border-blue-500 text-blue-600",
  };
  return (
    <div className={`bg-white rounded-2xl border-2 ${colors[color]} p-4 text-center`} data-testid={testid}>
      <div className="text-3xl font-extrabold">{value}</div>
      <div className="text-xs text-slate-500 mt-1">{label}</div>
    </div>
  );
}
